// Purchases and assets (Ordre 20): suppliers, and the assets Jamora owns or holds. Labels and pure helpers shared by the screens.
// The server stays the only authority (permissions, holder relation, rules); everything here only presents and prepares requests.
import { formatMinor, minorToText, toMinor } from "./finance";
import { hasPermission } from "./permissions";

export const SUPPLIER_CATEGORIES = [
  ["it", "Informatique"], ["telecom", "Télécommunications"], ["online", "Services en ligne"],
  ["furniture", "Mobilier"], ["services", "Prestations"], ["other", "Autre"],
];
export const ASSET_TYPES = [
  ["computer", "Ordinateur"], ["mobile", "Téléphone ou tablette"], ["peripheral", "Périphérique"], ["license", "Licence ou logiciel"],
  ["furniture", "Mobilier"], ["demo", "Matériel de démonstration"], ["other", "Autre"],
];
export const CURRENCIES = ["XOF", "EUR", "USD", "GBP", "CAD", "CHF"];

export const STATUS_LABELS = { in_stock: "En stock", assigned: "Affecté", loaned: "Prêté", retired: "Retiré" };
export const CONDITION_LABELS = { new: "Neuf", good: "Bon état", worn: "Usé", broken: "En panne" };
export const OWNER_KIND_LABELS = { company: "La société", collaborator: "Une personne de l'équipe", third_party: "Un tiers (bailleur, prêteur…)", to_confirm: "À confirmer" };
export const FUNDER_KIND_LABELS = { company: "La société", collaborator: "Une personne de l'équipe", third_party: "Un tiers", unknown: "Inconnu" };
export const RETIRE_REASON_LABELS = { scrapped: "Mis au rebut", sold: "Vendu", lost: "Perdu", stolen: "Volé", returned_to_owner: "Rendu à son propriétaire" };
export const WARRANTY_LABELS = { valid: "Sous garantie", ending: "Garantie bientôt finie", expired: "Garantie expirée" };

// What each movement is called on a button, and in the journal.
export const MOVE_LABELS = {
  entry: "Entrée en stock", assign: "Affecté", loan: "Prêté", return: "Restitué", transfer: "Transféré", location: "Emplacement changé",
  owner_change: "Propriétaire changé", condition: "État changé", retire: "Retiré", count: "Inventaire",
};
export const MOVE_ACTIONS = {
  assign: "Affecter", loan: "Prêter", return: "Restituer", transfer: "Transférer", location: "Changer l'emplacement",
  owner_change: "Changer le propriétaire", condition: "Changer l'état", retire: "Retirer de l'inventaire",
};

export const typeLabel = (code) => (ASSET_TYPES.find(([c]) => c === code) || [])[1] || code;
export const supplierCategoryLabel = (code) => (SUPPLIER_CATEGORIES.find(([c]) => c === code) || [])[1] || code;

// Feature on + the read key of the area. A write also needs the read key (D-60): the server refuses otherwise.
export const canSeeSuppliers = (user) => !!user?.features?.procurement && hasPermission(user, "procurement:view");
export const canManageSuppliers = (user) => canSeeSuppliers(user) && hasPermission(user, "procurement:manage");
export const canSeeInventory = (user) => !!user?.features?.procurement && hasPermission(user, "assets:view");
export const canManageAssets = (user) => canSeeInventory(user) && hasPermission(user, "assets:manage");
export const canSeeOwnAssets = (user) => !!user?.features?.procurement && hasPermission(user, ["assets:view_own", "assets:view"]);
export const canSeeAssets = (user) => canSeeInventory(user) || canSeeOwnAssets(user);

export function money(minor, currency) {
  return minor === null || minor === undefined || !currency ? "" : formatMinor(minor, currency);
}

// « Fatou Ndiaye », « Cabinet Diop (extérieur) », « En stock », « À confirmer »…
export function partyText(party, empty = "Non renseigné") {
  if (!party) return empty;
  if (party.kind === "stock") return "En stock";
  if (party.kind === "to_confirm") return "À confirmer";
  if (party.kind === "unknown") return "Inconnu";
  if (party.kind === "company") return "La société";
  if (party.kind === "external") return `${party.name} (extérieur)`;
  if (party.kind === "third_party") return `${party.name} (tiers)`;
  return party.name || empty;
}

export function statusTone(status) {
  return {
    in_stock: "bg-slate-50 text-slate-700 border-slate-200", assigned: "bg-sky-50 text-sky-800 border-sky-200",
    loaned: "bg-amber-50 text-amber-800 border-amber-200", retired: "bg-slate-100 text-slate-500 border-slate-200",
  }[status] || "bg-slate-50 text-slate-700 border-slate-200";
}

export function warrantyTone(state) {
  return { valid: "text-emerald-700", ending: "text-amber-700", expired: "text-rose-700" }[state] || "text-slate-500";
}

// Which movements the screen offers for an asset in this status (the server decides; this avoids offering what it will refuse).
export function availableMoves(asset) {
  switch (asset?.status) {
    case "in_stock": return ["assign", "loan", "location", "condition", "owner_change", "retire"];
    case "assigned": return ["transfer", "loan", "return", "location", "condition", "owner_change", "retire"];
    case "loaned": return ["return", "location", "condition", "owner_change"];
    default: return [];
  }
}

export const EMPTY_PARTY = { kind: "", collaborator_id: "", prospect_id: "", name: "" };

// A party the form builds: only the fields its kind uses.
export function partyPayload(party) {
  if (!party?.kind) return null;
  if (party.kind === "collaborator") return { kind: "collaborator", collaborator_id: party.collaborator_id };
  if (party.kind === "external" && party.prospect_id) return { kind: "external", prospect_id: party.prospect_id };
  if (party.kind === "third_party" || party.kind === "external") return { kind: party.kind, name: (party.name || "").trim() };
  return { kind: party.kind };
}

export function partyProblem(party, label) {
  if (!party?.kind) return `${label} : faites un choix.`;
  if (party.kind === "collaborator" && !party.collaborator_id) return `${label} : choisissez la personne.`;
  if ((party.kind === "third_party" || party.kind === "external") && !party.prospect_id && !(party.name || "").trim()) return `${label} : indiquez son nom.`;
  return "";
}

export const emptyAssetForm = () => ({
  type: "computer", brand: "", model: "", label: "", serial_number: "", note: "", cost: "", currency: "XOF", acquired_on: "", supplier_id: "",
  warranty_until: "", renewal_on: "", location: "", condition: "good", owner: { kind: "to_confirm", collaborator_id: "", name: "" },
  funder: { kind: "unknown", collaborator_id: "", name: "" },
});

export function assetFormProblem(form) {
  if (!form.type) return "Choisissez le type d'actif.";
  if (![form.label, form.brand, form.model].some((v) => (v || "").trim())) return "Donnez au moins un libellé, une marque ou un modèle.";
  if (form.cost.trim() && toMinor(form.cost, form.currency) === null) return "Le coût n'est pas un montant valide.";
  return partyProblem(form.owner, "Propriétaire") || partyProblem(form.funder, "Financeur");
}

const orNull = (v) => (typeof v === "string" ? v.trim() || null : v ?? null);

// The body of POST /procurement/assets: cost in minor units with its currency, or neither.
export function assetCreatePayload(form) {
  const cost = form.cost.trim() ? toMinor(form.cost, form.currency) : null;
  return {
    type: form.type, brand: orNull(form.brand), model: orNull(form.model), label: orNull(form.label), serial_number: orNull(form.serial_number),
    note: orNull(form.note), cost_minor: cost, currency: cost === null ? null : form.currency, acquired_on: orNull(form.acquired_on),
    supplier_id: orNull(form.supplier_id), warranty_until: orNull(form.warranty_until), renewal_on: orNull(form.renewal_on),
    location: orNull(form.location), condition: form.condition || "good", owner: partyPayload(form.owner), funder: partyPayload(form.funder),
  };
}

export const moveNeedsReason = (kind) => ["owner_change", "retire"].includes(kind);

export const emptyMoveForm = (kind, asset) => ({
  holder: { kind: kind === "assign" || kind === "loan" || kind === "transfer" ? "collaborator" : "", collaborator_id: "", name: "" },
  expected_return_on: "", location: asset?.location || "", owner: { kind: "company", collaborator_id: "", name: "" },
  condition: asset?.condition || "good", retire_reason: "scrapped", reason: "",
});

export function moveProblem(kind, form, today) {
  if (["assign", "loan", "transfer"].includes(kind)) {
    const p = partyProblem(form.holder, "Détenteur");
    if (p) return p;
  }
  if (kind === "loan") {
    if (!form.expected_return_on) return "Indiquez la date de retour prévue.";
    if (today && form.expected_return_on < today) return "La date de retour prévue ne peut pas être passée.";
  }
  if (kind === "location" && !form.location.trim()) return "Indiquez le nouvel emplacement.";
  if (kind === "owner_change") {
    const p = partyProblem(form.owner, "Propriétaire");
    if (p) return p;
  }
  if (moveNeedsReason(kind) && form.reason.trim().length < 3) return "Indiquez le motif (au moins 3 caractères).";
  return "";
}

// The body of POST /procurement/assets/{id}/moves.
export function movePayload(kind, form, asset) {
  const body = { kind, expected_version: asset.version };
  if (["assign", "loan", "transfer"].includes(kind)) body.holder = partyPayload(form.holder);
  if (kind === "loan") body.expected_return_on = form.expected_return_on;
  if (kind === "location") body.location = form.location.trim();
  if (kind === "owner_change") body.owner = partyPayload(form.owner);
  if (kind === "condition") body.condition = form.condition;
  if (kind === "retire") body.retire_reason = form.retire_reason;
  if (form.reason?.trim()) body.reason = form.reason.trim();
  return body;
}

export const supplierPayload = (form) => ({
  name: form.name.trim(), category: form.category, country: orNull(form.country), address: orNull(form.address), contact_name: orNull(form.contact_name),
  contact_email: orNull(form.contact_email), contact_phone: orNull(form.contact_phone), payment_terms: orNull(form.payment_terms), notes: orNull(form.notes),
});

export const emptySupplierForm = (supplier) => ({
  name: supplier?.name || "", category: supplier?.category || "it", country: supplier?.country || "", address: supplier?.address || "",
  contact_name: supplier?.contact_name || "", contact_email: supplier?.contact_email || "", contact_phone: supplier?.contact_phone || "",
  payment_terms: supplier?.payment_terms || "", notes: supplier?.notes || "",
});

export function supplierProblem(form) {
  if (form.name.trim().length < 2) return "Donnez le nom du fournisseur.";
  if (form.contact_email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim())) return "Cette adresse e-mail n'est pas valide.";
  return "";
}

export function procurementError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

export const newKey = () => `p-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;


// === Purchase requests (20-3) =====================================================================================================
// demande → devis → approbation → commande → réception → facture → une dépense. The server decides what each person may do
// (`capabilities` on every request); everything here only labels, prepares and checks what is typed before the round-trip.
export const REQUEST_STATUS_LABELS = {
  draft: "Brouillon", submitted: "À approuver", approved: "Approuvée", rejected: "Refusée", ordered: "Commandée", partially_received: "Reçue en partie",
  received: "Reçue", invoiced: "Facturée", closed: "Clôturée", cancelled: "Annulée",
};

export function requestStatusTone(status) {
  return {
    draft: "bg-slate-50 text-slate-700 border-slate-200", submitted: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200", rejected: "bg-rose-50 text-rose-800 border-rose-200",
    ordered: "bg-sky-50 text-sky-800 border-sky-200", partially_received: "bg-sky-50 text-sky-800 border-sky-200",
    received: "bg-sky-50 text-sky-800 border-sky-200", invoiced: "bg-violet-50 text-violet-800 border-violet-200",
    closed: "bg-slate-100 text-slate-500 border-slate-200", cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  }[status] || "bg-slate-50 text-slate-700 border-slate-200";
}

// The happy path, as a row of steps. A refused request waits at « Soumise »; a cancelled one has no current step.
const STEPS = [["draft", "Brouillon"], ["submitted", "Soumise"], ["approved", "Approuvée"], ["ordered", "Commandée"], ["received", "Reçue"], ["invoiced", "Facturée"], ["closed", "Clôturée"]];
const STEP_OF = { rejected: "submitted", partially_received: "ordered" };

export function requestSteps(status) {
  if (status === "cancelled") return STEPS.map(([id, label]) => ({ id, label, state: "todo" }));
  const current = STEPS.findIndex(([id]) => id === (STEP_OF[status] || status));
  const done = status === "closed" ? STEPS.length : current;
  return STEPS.map(([id, label], index) => ({ id, label, state: index < done ? "done" : index === current ? "current" : "todo" }));
}

export const REQUEST_SCOPES = [
  ["all", "Toutes"], ["mine", "Mes demandes"], ["to_approve", "À approuver"], ["to_order", "À commander"], ["to_receive", "À recevoir"], ["to_invoice", "À facturer"],
];

// Someone who only makes requests sees their own: one tab. Buyers and approvers get the working queues.
export const requestScopeTabs = (sawAll) => (sawAll ? REQUEST_SCOPES : REQUEST_SCOPES.filter(([id]) => id === "mine"));

export const REQUEST_HISTORY_LABELS = {
  request_created: "Demande créée", request_updated: "Demande modifiée", request_submitted: "Soumise pour approbation", request_approved: "Approuvée",
  request_rejected: "Refusée", approval_cancelled: "Approbation annulée après modification", request_cancelled: "Annulée", request_ordered: "Commandée",
  request_received: "Livraison enregistrée", assets_created: "Actifs créés depuis la livraison", request_invoiced: "Facture saisie",
  expense_recorded: "Dépense enregistrée dans les finances", request_closed: "Clôturée", quote_added: "Devis ajouté", quote_selected: "Devis retenu",
  quote_removed: "Devis retiré",
};

export const CONFORMITY_LABELS = { ok: "Conforme", claim: "À réclamer" };

const hasProcurement = (user) => !!user?.features?.procurement;
export const canMakeRequests = (user) => hasProcurement(user) && hasPermission(user, "procurement:create_request");
// Everyone who makes requests sees theirs; the buyers and the approvers see them all (and need the read key to act).
export const canSeeRequests = (user) => hasProcurement(user)
  && (hasPermission(user, "procurement:create_request") || (hasPermission(user, "procurement:view") && hasPermission(user, ["procurement:manage", "procurement:approve"])));

export const dateText = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

// --- The request form -----------------------------------------------------------------------------------------------------------
export const emptyLine = () => ({ key: newKey(), id: null, label: "", quantity: "1", unit_price: "" });

export const emptyRequestForm = () => ({ object: "", justification: "", needed_by: "", project: "", currency: "XOF", lines: [emptyLine()] });

export function requestFormFrom(request) {
  return {
    object: request.object, justification: request.justification, needed_by: request.needed_by || "", project: request.project || "", currency: request.currency,
    lines: request.lines.map((line) => ({ key: line.id, id: line.id, label: line.label, quantity: String(line.quantity), unit_price: minorToText(line.unit_price_minor, request.currency) })),
  };
}

const wholeNumber = (text) => (/^\d+$/.test((text || "").trim()) ? Number(text.trim()) : null);

// A unit price may be left empty or zero (the server accepts it); toMinor only knows positive amounts.
const unitMinor = (text, currency) => (/^\s*0*(?:[.,]0*)?\s*$/.test(text || "") ? 0 : toMinor(text, currency));

// One line in minor units, or null while it is not a valid one.
export function lineMinor(line, currency) {
  const quantity = wholeNumber(line.quantity);
  const unit = unitMinor(line.unit_price, currency);
  return quantity && quantity >= 1 && unit !== null ? { quantity, unit, total: quantity * unit } : null;
}

export function formTotalMinor(form) {
  return form.lines.reduce((sum, line) => sum + (lineMinor(line, form.currency)?.total || 0), 0);
}

export function requestFormProblem(form) {
  if (form.object.trim().length < 3) return "Donnez l'objet de la demande (au moins 3 caractères).";
  if (form.justification.trim().length < 3) return "Expliquez pourquoi cet achat est nécessaire.";
  if (!form.lines.length) return "Ajoutez au moins une ligne.";
  for (const [index, line] of form.lines.entries()) {
    if (line.label.trim().length < 2) return `Ligne ${index + 1} : donnez un libellé.`;
    if (!lineMinor(line, form.currency)) return `Ligne ${index + 1} : vérifiez la quantité (nombre entier) et le prix.`;
  }
  return "";
}

// The body of POST /procurement/requests (and, with the version, PATCH). A line keeps its server id when it has one.
export function requestPayload(form) {
  return {
    object: form.object.trim(), justification: form.justification.trim(), needed_by: form.needed_by || null, project: orNull(form.project), currency: form.currency,
    lines: form.lines.map((line) => {
      const m = lineMinor(line, form.currency);
      return { ...(line.id ? { id: line.id } : {}), label: line.label.trim(), quantity: m.quantity, unit_price_minor: m.unit };
    }),
  };
}

// --- Quotes ---------------------------------------------------------------------------------------------------------------------
export const emptyQuoteForm = () => ({ supplier_id: "", amount: "", valid_until: "", reference: "", note: "" });

export function quoteProblem(form, currency) {
  if (!form.supplier_id) return "Choisissez le fournisseur.";
  if (toMinor(form.amount, currency) === null) return "Le montant du devis n'est pas valide.";
  return "";
}

export function quotePayload(form, currency, version, fileIds) {
  return { expected_version: version, supplier_id: form.supplier_id, amount_minor: toMinor(form.amount, currency), valid_until: orNull(form.valid_until),
    reference: orNull(form.reference), note: orNull(form.note), file_ids: fileIds };
}

// Keeping a quote that is not the cheapest of the live ones needs a reason (the server insists too).
export function choiceNeedsReason(quote, quotes) {
  const best = Math.min(...quotes.map((q) => q.amount_minor));
  return quote.amount_minor > best;
}

// --- Order, delivery, assets, invoice -------------------------------------------------------------------------------------------
export const emptyOrderForm = (today) => ({ ordered_on: today, expected_delivery_on: "", reference: "" });

export function orderProblem(form, today) {
  if (!form.ordered_on) return "Indiquez la date de la commande.";
  if (form.ordered_on > today) return "La date de la commande ne peut pas être dans le futur.";
  if (form.expected_delivery_on && form.expected_delivery_on < form.ordered_on) return "La livraison prévue ne peut pas précéder la commande.";
  return "";
}

export const orderPayload = (form, version) => ({ expected_version: version, ordered_on: form.ordered_on, expected_delivery_on: orNull(form.expected_delivery_on), reference: orNull(form.reference) });

// What is still to receive, line by line, pre-filled with the whole remainder.
export function emptyReceptionForm(request, today) {
  return {
    received_on: today, conformity: "ok", note: "", received_by_name: "",
    lines: request.lines.filter((l) => l.quantity - l.received > 0).map((l) => ({ line_id: l.id, label: l.label, remaining: l.quantity - l.received, quantity: String(l.quantity - l.received) })),
  };
}

export function receptionProblem(form, today, orderedOn) {
  if (!form.received_on) return "Indiquez la date de réception.";
  if (form.received_on > today) return "La date de réception ne peut pas être dans le futur.";
  if (orderedOn && form.received_on < orderedOn) return "La réception ne peut pas précéder la commande.";
  const taken = form.lines.filter((l) => (l.quantity || "").trim() !== "" && l.quantity !== "0");
  if (!taken.length) return "Indiquez au moins une ligne reçue.";
  for (const line of taken) {
    const n = wholeNumber(line.quantity);
    if (!n) return `« ${line.label} » : la quantité doit être un nombre entier.`;
    if (n > line.remaining) return `« ${line.label} » : il ne reste que ${line.remaining} à recevoir.`;
  }
  if (form.conformity === "claim" && form.note.trim().length < 3) return "Décrivez ce qui est à réclamer.";
  return "";
}

export function receptionPayload(form, version) {
  return {
    expected_version: version, received_on: form.received_on, conformity: form.conformity, note: orNull(form.note), received_by_name: orNull(form.received_by_name),
    lines: form.lines.filter((l) => wholeNumber(l.quantity)).map((l) => ({ line_id: l.line_id, quantity: wholeNumber(l.quantity) })),
  };
}

// One editable row per asset that may still be made: nothing is created until the person confirms the rows they keep.
export function assetRowsFrom(request) {
  const rows = [];
  for (const line of request.lines) {
    for (let n = 0; n < Math.max(0, line.assets_left); n += 1) {
      rows.push({ key: `${line.id}-${n}`, line_id: line.id, include: false, type: "computer", brand: "", model: "", label: line.label, serial_number: "",
        cost: minorToText(line.unit_price_minor, request.currency), currency: request.currency });
    }
  }
  return rows;
}

export function assetRowsProblem(rows) {
  const kept = rows.filter((r) => r.include);
  if (!kept.length) return "Cochez au moins un actif à créer.";
  for (const row of kept) {
    if (![row.label, row.brand, row.model].some((v) => (v || "").trim())) return "Chaque actif a besoin d'un libellé, d'une marque ou d'un modèle.";
    if (row.cost.trim() && toMinor(row.cost, row.currency) === null) return "Un coût n'est pas un montant valide.";
  }
  return "";
}

export function assetRowsPayload(rows, version) {
  return {
    expected_version: version,
    items: rows.filter((r) => r.include).map((r) => {
      const cost = r.cost.trim() ? toMinor(r.cost, r.currency) : null;
      return { line_id: r.line_id, type: r.type, brand: orNull(r.brand), model: orNull(r.model), label: orNull(r.label), serial_number: orNull(r.serial_number),
        cost_minor: cost, currency: cost === null ? null : r.currency };
    }),
  };
}

export const emptyInvoiceForm = (today, request) => ({ reference: "", issued_on: today, amount: request?.quotes?.find((q) => q.selected) ? minorToText(request.quotes.find((q) => q.selected).amount_minor, request.currency) : "" });

export function invoiceProblem(form, currency, today, files) {
  if (!form.reference.trim()) return "Indiquez la référence de la facture.";
  if (!form.issued_on) return "Indiquez la date de la facture.";
  if (form.issued_on > today) return "La date de la facture ne peut pas être dans le futur.";
  if (toMinor(form.amount, currency) === null) return "Le montant de la facture n'est pas valide.";
  if (!files.length) return "Joignez la facture.";
  return "";
}

export const invoicePayload = (form, currency, version, fileIds) => ({
  expected_version: version, reference: form.reference.trim(), issued_on: form.issued_on, amount_minor: toMinor(form.amount, currency), file_ids: fileIds,
});

// --- Paramètres › Achats (20-4) ---------------------------------------------------------------------------------------------------
// The server stores «XOF,EUR» and «XOF=100000;EUR=150» (whole units of the currency); the screen edits them as checkboxes and one field each.
export const parseCurrencies = (text) => (text || "").split(",").map((c) => c.trim()).filter(Boolean);
export const joinCurrencies = (selected) => CURRENCIES.filter((c) => selected.includes(c)).join(",");

export function parseThreshold(text) {
  const out = {};
  for (const part of (text || "").split(";").filter(Boolean)) {
    const [currency, amount] = part.split("=");
    if (currency) out[currency] = amount || "";
  }
  return out;
}

// Only the accepted currencies with an amount; empty = every request goes to an approver.
export function joinThreshold(values, accepted) {
  return accepted.filter((c) => String(values[c] ?? "").trim() !== "").map((c) => `${c}=${String(values[c]).trim()}`).join(";");
}

export function thresholdProblem(values, accepted) {
  const bad = accepted.find((c) => String(values[c] ?? "").trim() !== "" && !/^\d{1,12}$/.test(String(values[c]).trim()));
  return bad ? `Seuil ${bad} : un nombre entier, sans espace ni décimale.` : null;
}

export const procurementAlertDays = (text) => (text || "").split(",").map((p) => p.trim()).filter(Boolean).join(",");

export function procurementAlertDaysProblem(text) {
  const value = procurementAlertDays(text);
  if (!value) return "Indiquez au moins un nombre de jours (le jour même s'y ajoute toujours).";
  const parts = value.split(",");
  if (parts.length > 5 || parts.some((p) => !/^\d{1,3}$/.test(p) || Number(p) > 365)) return "Jusqu'à 5 nombres de jours entre 0 et 365, séparés par des virgules.";
  return null;
}

export function settingsProblem(selected, thresholds, days) {
  if (selected.length === 0) return "Gardez au moins une devise.";
  return thresholdProblem(thresholds, selected) || procurementAlertDaysProblem(days);
}

// --- Inventory (20-4) -------------------------------------------------------------------------------------------------------------
export const INVENTORY_STATUS_LABELS = { open: "Ouvert", closed: "Terminé", cancelled: "Annulé" };
export const COUNT_RESULT_LABELS = { found: "Trouvé", absent: "Absent", moved: "Ailleurs", unchecked: "À pointer" };
export const COUNT_RESULT_TONES = {
  found: "bg-emerald-50 text-emerald-800 border-emerald-200", absent: "bg-rose-50 text-rose-800 border-rose-200",
  moved: "bg-amber-50 text-amber-800 border-amber-200", unchecked: "bg-slate-50 text-slate-600 border-slate-200",
};
export const countResultOf = (line) => line.result || "unchecked";

// «Terminer» with unpointed assets asks for a confirmation; the gaps (absent, elsewhere) are only reported, never applied.
export const inventoryGaps = (counts) => (counts?.absent || 0) + (counts?.moved || 0);

export function markProblem(result, location) {
  if (result === "moved" && !(location || "").trim()) return "Indiquez où l'actif a été trouvé.";
  return null;
}

export const EXPORT_KINDS = [["requests", "Demandes d'achat"], ["assets", "Actifs"], ["suppliers", "Fournisseurs"]];

// Link between the expenses' free-text suppliers and the referential (Finance side): who may reconcile them, and the sentence that says it.
export const canLinkSuppliers = (user) => canManageSuppliers(user) && !!user?.features?.finance && hasPermission(user, "finance:view_all");
export const canSeeSupplierSpend = (user) => canSeeSuppliers(user) && !!user?.features?.finance && hasPermission(user, "finance:view_all");

export function unlinkedSummary(items) {
  const names = (items || []).length;
  if (!names) return "";
  const expenses = items.reduce((n, i) => n + (i.expenses || 0), 0);
  const subscriptions = items.reduce((n, i) => n + (i.subscriptions || 0), 0);
  const parts = [`${expenses} dépense${expenses > 1 ? "s" : ""}`, subscriptions ? `${subscriptions} abonnement${subscriptions > 1 ? "s" : ""}` : null].filter(Boolean);
  return `${names} nom${names > 1 ? "s" : ""} de fournisseur sans fiche, sur ${parts.join(" et ")}`;
}

export const linkedMessage = ({ expenses = 0, subscriptions = 0 }) => {
  const parts = [`${expenses} dépense${expenses > 1 ? "s" : ""}`, subscriptions ? `${subscriptions} abonnement${subscriptions > 1 ? "s" : ""}` : null].filter(Boolean);
  if (!expenses && !subscriptions) return "Rien à rattacher : déjà fait";
  return `${parts.join(" et ")} ${expenses + subscriptions > 1 ? "rattachés" : expenses ? "rattachée" : "rattaché"}`;
};
